import { Type, TypeFrom } from '@willow/types-iso';

import { FunctionContext, SharedFunctionContext } from './FunctionContext';

export interface SpreadParam<P extends Parameter<Type<unknown>>> {
  spread: P;
}

export function spreadParam<P extends Parameter<Type<unknown>>>(param: P): SpreadParam<P> {
  return { spread: param };
}

export interface SpreadType<T extends Type<unknown>> {
  spread: T;
}

export function spreadType<T extends Type<unknown>>(type: T): SpreadType<T> {
  return { spread: type };
}

export type StrictParameter<T extends Type<unknown>> = {
  name: string;
  type: T;
  isLazy?: false;
};

/**
 * A lazy parameter is not evaluated until the function is called.
 *
 * This is necessary to support short-circuiting, where the value of a parameter
 * is not evaluated immediately as input for a function. Example:
 *
 *     if(
 *       isValidPlainDate([someString]),
 *       plainDate([someString]),
 *       plainDate('1970-01-01')
 *     )
 *
 * Here, [someString] may or may not be valid. If the second parameter is
 * evaluated before being passed into the `if` function then an error will be
 * thrown before the condition can be tested. Making these parameters lazy allows
 * us to evaluate the condition before the other parameters, preventing the error
 */
export type LazyParameter<T extends Type<unknown>> = {
  name: string;
  type: T;
  isLazy: true;
};

export type Parameter<T extends Type<unknown>> = StrictParameter<T> | LazyParameter<T>;

/**
 * `Ctx` is the slice of evaluator context the function's implementation needs. It defaults to
 * `SharedFunctionContext` (loan + time), which every evaluator can satisfy, so a function authored
 * with `functionFrom` cannot reach for workflow-only context. Workflow-only functions opt into the
 * fuller `FunctionContext` via `workflowFunctionFrom`. Because `implementation` is contravariant in
 * `Ctx`, a shared definition is assignable to a workflow library but not vice-versa — which is what
 * keeps workflow functions out of the email registry at compile time.
 */
export interface FunctionDefinition<
  Params extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
  ReturnType extends Type<unknown>,
  Name extends string,
  Ctx = SharedFunctionContext,
> {
  name: Name;
  parameters: Params;
  returnType: ReturnType;
  implementation: (ctx: Ctx, ...args: TypeFromParams<Params>) => TypeFrom<ReturnType>;
  documentation: string;
}

export function functionFrom<
  Params extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
  ReturnType extends Type<unknown>,
  Name extends string,
  Ctx = SharedFunctionContext,
>(
  name: Name,
  parameters: Params,
  returnType: ReturnType,
  implementation: (ctx: Ctx, ...args: TypeFromParams<Params>) => TypeFrom<ReturnType>,
  documentation: string,
): FunctionDefinition<Params, ReturnType, Name, Ctx> {
  return {
    name,
    parameters,
    returnType,
    implementation,
    documentation,
  };
}

/**
 * Like {@link functionFrom}, but the implementation receives the full {@link FunctionContext}
 * including workflow-only fields (steps, launch time, point of contact). Functions declared this way
 * can only live in the workflow registry.
 */
export function workflowFunctionFrom<
  Params extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
  ReturnType extends Type<unknown>,
  Name extends string,
>(
  name: Name,
  parameters: Params,
  returnType: ReturnType,
  implementation: (ctx: FunctionContext, ...args: TypeFromParams<Params>) => TypeFrom<ReturnType>,
  documentation: string,
): FunctionDefinition<Params, ReturnType, Name, FunctionContext> {
  return {
    name,
    parameters,
    returnType,
    implementation,
    documentation,
  };
}

export function getFunctionSignature<
  Params extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
  ReturnType extends Type<unknown>,
  Name extends string,
>({
  name,
  parameters,
  returnType,
}: {
  name: Name;
  parameters: Params;
  returnType: ReturnType;
}): FunctionSignatureFrom<Params, ReturnType, Name> {
  return `${getFunctionSignatureWithoutReturnType({
    name,
    parameterTypes:
      'spread' in parameters
        ? ({ spread: parameters.spread.type } as SpreadType<Type<unknown>>)
        : parameters.map((p): Type<unknown> => p.type),
  })} -> ${returnType.name as ReturnType['name']}` as FunctionSignatureFrom<Params, ReturnType, Name>;
}

export type FunctionSignatureFromWithoutReturnType<
  Params extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
  Name extends string,
> = `${Name}(${ParamSignatureFrom<Params>})`;

export type FunctionSignatureFrom<
  Params extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
  ReturnType extends Type<unknown>,
  Name extends string,
> = `${Name}(${ParamSignatureFrom<Params>}) -> ${ReturnType['name']}`;

export type ParamSignatureFrom<Params extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>> =
  Params extends SpreadParam<infer U>
    ? `...${U['type']['name']}[]`
    : Params extends [...infer Rest]
      ? Rest extends [...Parameter<Type<unknown>>[]]
        ? ParamSignatureFromArgList<Rest>
        : never
      : never;

export type ParamSignatureFromArgList<Params extends [...Parameter<Type<unknown>>[]]> = Params extends []
  ? ''
  : Params extends [Parameter<infer U>, Parameter<infer V>, ...infer Rest]
    ? Rest extends [...Parameter<Type<unknown>>[]]
      ? `${U['name']}, ${ParamSignatureFromArgList<[Parameter<V>, ...Rest]>}`
      : never
    : Params extends [Parameter<infer U>]
      ? `${U['name']}`
      : never;

export function getFunctionSignatureWithoutReturnType<
  ParamTypes extends [...Type<unknown>[]] | SpreadType<Type<unknown>>,
  Name extends string,
>({ name, parameterTypes }: { name: Name; parameterTypes: ParamTypes }): string {
  return `${name}(${
    'spread' in parameterTypes ? `...${parameterTypes.spread.name}[]` : parameterTypes.map((p) => p.name).join(', ')
  })`;
}

export type TypeFromParams<T extends [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>> =
  T extends SpreadParam<Parameter<infer U>>
    ? [...U[]]
    : T extends []
      ? []
      : T extends [LazyParameter<Type<infer U>>, ...infer Rest]
        ? Rest extends [...Parameter<Type<unknown>>[]]
          ? [() => U, ...TypeFromParams<Rest>]
          : never
        : T extends [StrictParameter<Type<infer U>>, ...infer Rest]
          ? Rest extends [...Parameter<Type<unknown>>[]]
            ? [U, ...TypeFromParams<Rest>]
            : never
          : [];

// A function definition with its parameter/return/name types erased — the shape stored in a
// registry and resolved at parse/eval time.
export type AnyFunctionDefinition<Ctx = SharedFunctionContext> = FunctionDefinition<
  [...Parameter<Type<unknown>>[]] | SpreadParam<Parameter<Type<unknown>>>,
  Type<unknown>,
  string,
  Ctx
>;

export type FunctionLibrary<Ctx = SharedFunctionContext> = Record<string, AnyFunctionDefinition<Ctx>>;
